import { Injectable, NgZone } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { ToastrService } from 'ngx-toastr'
import { mergeMap } from 'rxjs'
import { tap } from 'rxjs/operators'
import { IJWTToken, IUser } from '../../interfaces/user.interface'
import { UsersService } from '../../users.service'
import { SetBookmarks } from '../bookmarks/bookmarks.actions'
import {
  GetCurrentUser,
  Login,
  Register,
  Logout,
  RequestPasswordReset,
  ResetPassword,
  UpdateProfile,
  UpdatePassword,
  SetReturnUrl,
  NavigateToReturnUrl,
  GetUserPreferences,
  UpdateUserPreferences,
  DeleteUser,
  UpdateUser,
  GetAdminUserPreferences,
  UpdateAdminUserPreferences,
  PublicRegister,
  RefreshToken,
  SetTokenContent
} from './user-account.actions'
import { UserAccountStateModel } from './user-account.model'
import { IUserPreferencesResponse } from '../../interfaces/user-preferences.interface'
import { LOCAL_STORAGE_KEYS } from '@lla-platform/core/core-util'
import { jwtDecode } from 'jwt-decode'
import { isEqual, sortBy } from 'lodash'
import { MatDialog } from '@angular/material/dialog'
import { RefreshWebsiteDialogComponent } from '../../components/refresh-website/refresh-website-dialog.component'

@State<UserAccountStateModel>({
  name: 'userAccount'
})
@Injectable()
export class UserAccountState {
  constructor(
    private usersService: UsersService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private toastrService: ToastrService,
    private store: Store,
    private dialogService: MatDialog
  ) {}

  @Selector()
  static isAuthenticated(state: UserAccountStateModel) {
    return state.isAuthenticated
  }

  @Selector()
  static currentUser(state: UserAccountStateModel) {
    return state.user
  }

  @Selector()
  static userPreferences(state: UserAccountStateModel): IUserPreferencesResponse {
    return (
      state.userPreferences ?? {
        preferences: [],
        availablePreferences: []
      }
    )
  }

  @Selector()
  static permissions(state: UserAccountStateModel) {
    return state.user?.permissions ?? []
  }

  @Selector()
  static shouldReload(state: UserAccountStateModel) {
    return state.shouldReload
  }

  @Action(Login)
  login(ctx: StateContext<UserAccountStateModel>, { payload }: Login) {
    return this.usersService.login(payload).pipe(
      mergeMap((res) => {
        this.setAuthToken(res.token)
        return ctx.dispatch(new GetCurrentUser())
      })
    )
  }

  @Action(Logout)
  logout(ctx: StateContext<UserAccountStateModel>) {
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
    }
    ctx.patchState({ user: undefined, isAuthenticated: false, shouldReload: true })
  }

  @Action(GetCurrentUser)
  getCurrentUser(ctx: StateContext<UserAccountStateModel>) {
    return this.usersService.getFullUserInformation().pipe(
      tap((res: IUser) => {
        const token = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
        if (!token) {
          return
        }
        const tokenContent = jwtDecode<IJWTToken>(token)
        ctx.patchState({
          user: {
            ...res,
            role: tokenContent.role,
            permissions: tokenContent.UserPermissions,
            locationPermissions: tokenContent.LocationPermissions
          },
          isAuthenticated: true
        })
        this.store.dispatch(new SetBookmarks(res.bookmarks))
      })
    )
  }

  @Action(Register)
  register(ctx: StateContext<UserAccountStateModel>, { payload }: Register) {
    return this.usersService.register(payload)
  }

  @Action(PublicRegister)
  publicRegister(ctx: StateContext<UserAccountStateModel>, { payload }: PublicRegister) {
    return this.usersService.publicRegister(payload).pipe(
      mergeMap((res) => {
        this.setAuthToken(res.token)
        return ctx.dispatch(new GetCurrentUser())
      })
    )
  }

  @Action(DeleteUser)
  deleteUser(ctx: StateContext<UserAccountStateModel>, { userId }: DeleteUser) {
    return this.usersService.deleteUser(userId)
  }

  @Action(UpdateUser)
  updateUser(ctx: StateContext<UserAccountStateModel>, { payload }: UpdateUser) {
    return this.usersService.updateUser(payload)
  }

  @Action(RequestPasswordReset)
  requestPasswordReset(ctx: StateContext<UserAccountStateModel>, { payload }: RequestPasswordReset) {
    return this.usersService.requestPasswordReset(payload)
  }

  @Action(ResetPassword)
  resetPassword(ctx: StateContext<UserAccountStateModel>, { payload }: ResetPassword) {
    return this.usersService.resetPassword(payload).pipe(
      mergeMap((res) => {
        this.setAuthToken(res.token)
        return ctx.dispatch(new GetCurrentUser())
      })
    )
  }

  @Action(UpdateProfile)
  updateProfile(ctx: StateContext<UserAccountStateModel>, { payload }: UpdateProfile) {
    return this.usersService
      .updateProfile(payload)
      .pipe(mergeMap(() => ctx.dispatch(new GetCurrentUser())))
  }

  @Action(UpdatePassword)
  updatePassword(ctx: StateContext<UserAccountStateModel>, { payload }: UpdatePassword) {
    return this.usersService.updatePassword(payload)
  }

  @Action(GetUserPreferences)
  getUserPreferences(ctx: StateContext<UserAccountStateModel>) {
    return this.usersService.getUserPreferences().pipe(
      tap((res) => {
        ctx.patchState({ userPreferences: res })
      })
    )
  }

  @Action(UpdateUserPreferences, { cancelUncompleted: true })
  updateUserPreferences(ctx: StateContext<UserAccountStateModel>, { payload }: UpdateUserPreferences) {
    return this.usersService.updateUserPreferences({ preferences: payload })
  }

  @Action(RefreshToken)
  refreshToken(ctx: StateContext<UserAccountStateModel>) {
    return this.usersService.refreshToken().pipe(
      tap((res) => {
        const user = ctx.getState()?.user
        if (!res.token || !user) {
          return
        }
        localStorage.setItem(LOCAL_STORAGE_KEYS.JWT_TOKEN, res.token)

        this.store.dispatch(new SetTokenContent())
      })
    )
  }

  @Action(SetTokenContent)
  setTokenContent(ctx: StateContext<UserAccountStateModel>) {
    const user = ctx.getState()?.user
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)

    if (!token || !user) {
      return
    }

    const tokenContent = jwtDecode<IJWTToken>(token)

    if (
      !isEqual(
        [...sortBy(user.locationPermissions ?? [])],
        [...sortBy(tokenContent?.LocationPermissions ?? [])]
      )
    ) {
      this.openRefreshModal()
    }

    if (
      !isEqual([...sortBy(user.permissions ?? [])], [...sortBy(tokenContent?.UserPermissions ?? [])])
    ) {
      this.openRefreshModal()
    }

    ctx.patchState({
      user: {
        ...user,
        role: tokenContent.role,
        permissions: tokenContent.UserPermissions,
        locationPermissions: tokenContent.LocationPermissions
      },
      isAuthenticated: true
    })
  }

  @Action(SetReturnUrl)
  setReturnUrl(ctx: StateContext<UserAccountStateModel>) {
    const { returnUrl } = ctx.getState()
    if (returnUrl && returnUrl !== '/') {
      return
    }
    const fromQuery: string = this.activatedRoute.snapshot.queryParams['returnUrl']
    if (
      fromQuery &&
      !(
        fromQuery.startsWith(`${window.location.origin}/`) ||
        /\/[^\\/].*/.test(fromQuery) ||
        fromQuery === '/'
      )
    ) {
      // This is an extra check to prevent open redirects.
      this.toastrService.error(
        'Invalid return url. The return url needs to have the same origin as the current page.'
      )
      ctx.patchState({
        returnUrl: '/'
      })
      return
    }

    ctx.patchState({
      returnUrl: fromQuery || '/'
    })
  }

  @Action(NavigateToReturnUrl)
  async navigateToReturnUrl(ctx: StateContext<UserAccountStateModel>) {
    await this.ngZone.run(() =>
      this.router.navigateByUrl(ctx.getState()?.returnUrl ?? '/', {
        replaceUrl: true
      })
    )
    ctx.patchState({
      returnUrl: undefined
    })
  }

  @Action(GetAdminUserPreferences)
  getAdminUserPreferences(
    ctx: StateContext<UserAccountStateModel>,
    { userId }: GetAdminUserPreferences
  ) {
    return this.usersService.getAdminUserPreferences(userId).pipe(
      tap((res) => {
        ctx.patchState({ userPreferences: res })
      })
    )
  }

  @Action(UpdateAdminUserPreferences, { cancelUncompleted: true })
  updateAdminUserPreferences(
    ctx: StateContext<UserAccountStateModel>,
    { userId, payload }: UpdateAdminUserPreferences
  ) {
    return this.usersService.updateAdminUserPreferences(userId, { preferences: payload }).pipe(
      tap(() => {
        ctx.patchState({
          userPreferences: {
            preferences: payload,
            availablePreferences: ctx.getState()?.userPreferences?.availablePreferences ?? []
          }
        })
      })
    )
  }

  private setAuthToken(token: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.JWT_TOKEN, token)
  }

  private openRefreshModal() {
    this.dialogService.open(RefreshWebsiteDialogComponent, {
      autoFocus: false,
      panelClass: 'normal-modal',
      disableClose: true
    })
  }
}
